import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { api } from 'helpers/axios';
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { en, de } from 'yup-locales';
import { errorMessage } from "helpers/common";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import { useOutletContext } from "react-router-dom";
import { helpTextIcon } from "helpers/helpText";
import { useTranslation } from "translation/useTranslation";
import { Field, Input, CurrencyField, Checkbox, Button, SectionHeading, FormGroupBox } from "components/ui";
import { getLang } from "store/userSlice";

export default function BudgetGroupForm({ id, handleChangeSelectedBudget, fetchAllBudgets, batchUpdateCheckedList = [], handleEnableBatchUpdate = null }) {
  const lang = useSelector(getLang);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);

  const { helps } = useOutletContext();

  const { translate, editableTranslate } = useTranslation();

  const isBatchUpdate = useMemo(() => batchUpdateCheckedList.length > 0, [batchUpdateCheckedList]);

  const [batchUpdateFields, setBatchUpdateFields] = useState([]);

  const schema = yup.object({
    name: yup.string().required().label(translate('budget_setting.budget_group_name')),
    budget_amount: yup.string().required()
      // .transform((_value, originalValue) => Number(originalValue.replace(/\./g, '').replace(/,/, '.')))
      .test('is-decimal', translate('form.valid_format_decimal_2'), value => (value.toString()).match(/^\d*(,{1}(\d{1,2}))?$/))
      .label(translate('budget_setting.budget_amount')),
    // react-hook-form gives a checkbox group with nothing checked the value `false`; send `[]` so it clears
    budget_distributions: yup.array(yup.string()).transform(value => (value === false ? [] : value)).notRequired()
  });

  const { register, watch, handleSubmit, control, reset, formState: { errors }, getValues } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const fetchGroup = async (id) => {
    setLoading(true);
    try {
      const { data, status } = await api.get(`/settings/budget-groups/${id}`);
      if (status === 200) {
        reset(data);
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      setError(errorMessage(err, 'fetch budget groups'));
      Sentry.captureException(err);
    }
  };

  const watchBudgetDistribution = watch('budget_distributions') || [];

  useEffect(() => {
    if (id) {
      fetchGroup(id);
    }
  }, [id]);

  useEffect(() => {
    // budget_distributions logic
    // 1. pay_factor, 2. work_hours, 3. number_of_employees, 4. annual_compensation
    // If 1 and / or 2 is selected, checkbox 3 and 4 must be disabled
    // If 3 is selected, checkbox 1, 2, 4 must be disabled
    // If 4 is selected, checkbox 1, 2, 3 must be disabled

    const checkboxes = document.querySelectorAll('input[name="budget_distributions"]');

    // Convert NodeList to array for easier manipulation
    const checkboxesArray = Array.from(checkboxes);

    // Define the mutually exclusive groups
    const group1 = ["pay_factor", "work_hours"];
    const group2 = ["number_of_employees"];
    const group3 = ["annual_compensation"];

    // Get selected values
    const selectedValues = checkboxesArray
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    checkboxesArray.forEach(checkbox => {
      if (selectedValues.includes("number_of_employees")) {
        // Disable all except "number_of_employees"
        checkbox.disabled = !group2.includes(checkbox.value);
      } else if (selectedValues.includes("annual_compensation")) {
        // Disable all except "annual_compensation"
        checkbox.disabled = !group3.includes(checkbox.value);
      } else if (selectedValues.some(value => group1.includes(value))) {
        // If either "pay_factor" or "work_hours" is selected, disable "number_of_employees" and "annual_compensation"
        checkbox.disabled = group2.includes(checkbox.value) || group3.includes(checkbox.value);
      } else {
        // Enable all checkboxes if no conflicting selections exist
        checkbox.disabled = false;
      }
    });
  }, [watchBudgetDistribution]);

  const onSubmit = async (data) => {
    setError(null);
    setSaveLoading(true);

    if (!data.budget_amount.toString().includes(',')) data.budget_amount = data.budget_amount + ',00';

    const formData = { ...data };
    if (id) formData.hash_id = id;

    try {
      const { status } = await api.post(`/settings/budget-groups`, formData);
      if (status === 200) {
        fetchAllBudgets();
        toast.success(translate('form.success.saved'));
        handleChangeSelectedBudget(null);
      }
    } catch (err) {
      setSaveLoading(false);
      setError(errorMessage(err, 'save budget group'));
      Sentry.captureException(err);
    }
  };

  const handleCheckBatchUpdateFields = (e, field) => {
    if (e.target.checked) {
      const newBatchUpdateFields = [...batchUpdateFields];
      newBatchUpdateFields.push(field);
      setBatchUpdateFields(newBatchUpdateFields);
    } else {
      setBatchUpdateFields(batchUpdateFields.filter(f => f !== field));
    }
  };

  const handleBatchUpdateSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setSaveLoading(true);

    const data = getValues();

    if (data.budget_amount && !data.budget_amount.toString().includes(',')) data.budget_amount = data.budget_amount + ',00';

    const formData = {
      hash_ids: batchUpdateCheckedList.map(l => l.replace('group-', '')),
      ...data
    };

    if (!formData.budget_distributions) delete formData.budget_distributions;

    try {
      const { status } = await api.post(`/settings/budget-groups/batch-update`, formData);
      if (status === 200) {
        fetchAllBudgets();
        toast.success(translate('form.success.saved'));
        handleEnableBatchUpdate();
      }
    } catch (err) {
      setSaveLoading(false);
      setError(errorMessage(err, 'save budget group batch update'));
      Sentry.captureException(err);
    }
  };

  const getLabel = (key, translation_key, fallback) => {
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;

    return <>
      {editableTranslate(translation_key, fallback)}
      {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
    </>
  };

  return (
    <div className={`bg-white w-100 p-4 content-card`}>
      {
        error && <div>
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </div>
      }
      <SectionHeading
        title={<>
          {isBatchUpdate && <span className="mr-1">({editableTranslate('budget_setting.batch_update')})</span>}
          {editableTranslate('budget_setting.budget_group')}
        </>}
        desc={editableTranslate('budget_setting.budget_group_description')}
      />

      {
        loading
          ? <div className="text-center" data-testid="loading">
            <div className="spinner-border" role="status">
              <span className="sr-only">Wird geladen...</span>
            </div>
          </div>
          : <form onSubmit={isBatchUpdate ? handleBatchUpdateSubmit : handleSubmit(onSubmit)} className="pt-3">
            <FormGroupBox>
              <div className="peb-field-row">
                {
                  !isBatchUpdate &&
                  <Field label={editableTranslate('budget_setting.budget_group_name')} htmlFor="group-name" error={errors?.name?.message}>
                    <Input id="group-name" maxLength={255} error={!!errors?.name} {...register('name')} />
                  </Field>
                }
                <Field
                  label={getLabel('budget_setting.group_budget_amount', 'budget_setting.budget_amount')}
                  prefix={isBatchUpdate && <input type="checkbox" className="mr-1 align-middle" checked={batchUpdateFields.includes('budget_amount')} onChange={(e) => handleCheckBatchUpdateFields(e, 'budget_amount')} data-testid="batch-update-field-budget-amount" />}
                  error={errors?.budget_amount?.message}
                >
                  <Controller
                    control={control}
                    name="budget_amount"
                    render={({ field: { onChange, value } }) => (
                      <CurrencyField data-testid="budget-amount-input"
                        value={value}
                        maxLength={16}
                        disabled={isBatchUpdate && !batchUpdateFields.includes('budget_amount')}
                        onValueChange={(value, name, values) => onChange(value || null)}
                        error={!!errors?.budget_amount}
                      />
                    )}
                  />
                </Field>
              </div>
            </FormGroupBox>
            <SectionHeading
              className="mt-4"
              title={getLabel('budget_setting.budget_distributions', 'budget_setting.budget_distributions')}
              desc={editableTranslate('budget_setting.budget_distributions_description')}
            />
            <div>
              <div className="mb-5 mt-3 d-flex flex-column">
                <Checkbox id="distribution-pay_factor" value="pay_factor" {...register(`budget_distributions`)} label={getLabel('budget_setting.budget_distributions.pay_factor', 'field.pay_factor')} />
                <Checkbox id="distribution-work_hours" value="work_hours" {...register(`budget_distributions`)} label={getLabel('budget_setting.budget_distributions.work_hours', 'field.working_hours')} />
                <Checkbox id="distribution-number_of_employees" value="number_of_employees" {...register(`budget_distributions`)} label={editableTranslate('budget_setting.number_of_employees')} />
                <Checkbox id="distribution-annual_compensation" value="annual_compensation" {...register(`budget_distributions`)} label={editableTranslate('budget_setting.annual_compensation')} />
              </div>
              <div className="text-right">
                <Button variant="secondary" data-testid="cancel-button" className="mr-2" onClick={() => isBatchUpdate ? handleEnableBatchUpdate() : handleChangeSelectedBudget(null)}>{editableTranslate('button.cancel')}</Button>
                <Button variant="primary" type="submit" loading={saveLoading} data-testid="save-button">{editableTranslate('button.save')}</Button>
              </div>
            </div>
          </form>
      }
    </div>
  );
}
